import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, Users, ChevronRight, Target } from 'lucide-react';

export default function MyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { document.title = "Dashboard — Pools FC"; }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dashboardAPI.myDashboard();
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="space-y-4">{[1,2].map(i => <div key={`skel-${i}`} className="h-32 rounded-2xl bg-muted animate-pulse" />)}</div>;

  const pools = data?.pools || [];

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-2">My Dashboard</h1>
      <p className="text-muted-foreground text-sm mb-6">Your pools, entries, and standings at a glance.</p>

      {pools.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">No Pools Yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Join a pool to start competing!</p>
            <Link to="/browse" className="text-[#5b3cc4] font-medium hover:underline">Browse Pools →</Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pools.map(({ pool, team_alias, entries, total_entries_in_pool }) => (
            <Link to={`/pools/${pool.id}`} key={pool.id}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-[#5b3cc4]">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold">{pool.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {team_alias && <span className="text-sm text-[#5b3cc4] font-medium">{team_alias}</span>}
                        <Badge className={pool.status === 'open' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-yellow-100 text-yellow-700 text-[10px]'}>{pool.status}</Badge>
                        <Badge className="bg-[#f3f0ff] text-[#5b3cc4] text-[10px]">{pool.plan_label || 'Free'}</Badge>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {entries.map(entry => (
                      <div key={entry.id} className="rounded-xl bg-secondary/40 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-muted-foreground">Entry {entry.entry_number}</span>
                          {entry.rank > 0 && <Badge variant="secondary" className="text-[10px]">#{entry.rank}</Badge>}
                        </div>
                        <div className="font-heading text-xl font-bold text-[#5b3cc4]">{entry.total_points || 0}</div>
                        <div className="text-[10px] text-muted-foreground">pts</div>
                        {entry.picks && entry.picks.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {entry.picks.map((p, i) => (
                              <span key={i} className="text-[9px] bg-white rounded px-1 py-0.5 border">{p.team_name?.split(' ')[0]}</span>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2 text-[10px] text-yellow-600 font-medium flex items-center gap-1"><Target className="h-3 w-3" />Picks needed</div>
                        )}
                        {entry.goals_prediction && <div className="text-[9px] text-muted-foreground mt-1">Goals: {entry.goals_prediction}</div>}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{total_entries_in_pool} entries in pool</span>
                    <span>by {pool.organizer_name}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
